import {
  updateProject,
  analyseProject,
  updateCarbonFootprint,
  projectHasCarbonFigure,
  ProjectSlice,
} from "../src/features/projects/projectSlice";
import { carbonMaterialOf, decideCarbonWork } from "./carbonTriggers";
import { exactBarCheckMessage } from "../src/features/projects/exactBarChecks";

// Actions in ProjectSlice that do NOT trigger a backend save.
// Every OTHER ProjectSlice action is assumed to mutate persistable project input and will
// schedule a debounced auto-save (PUT to the server). This mirrors the output-invalidation
// model below (default-on, short denylist) so that any new user-editable reducer persists
// automatically — previously this was an allowlist, and every new setting silently failed to
// save until it was hand-added, which is why changes only "stuck" once an analysis re-POSTed
// the whole project. Keep this list short — when in doubt, let the action save.
const SAVE_EXCLUDED_ACTION_TYPES = new Set<string>([
  "ProjectSlice/pruneOutput", // middleware bookkeeping; the originating input action already schedules the save
  "ProjectSlice/setProject", // loads a project from the server — must not immediately echo it back
  "ProjectSlice/setActiveLCOs", // derived UI selection stored outside state.project; nothing project-side to persist
  // Clearing a toast message. These touch only the transient calcError/calcWarning fields, which
  // live outside state.project — there is nothing to persist, and saving here would PUT the
  // project again for a purely visual event.
  "ProjectSlice/dismissCalcError",
  "ProjectSlice/dismissCalcWarning",
  // Clearing the carbon figure; dispatched from this middleware for the input change that already
  // schedules its own save.
  "ProjectSlice/resetCarbonFootprint",
]);

// Actions in ProjectSlice that do NOT invalidate output.
// Every other ProjectSlice action is assumed to mutate input and will trigger pruneOutput.
// Keep this list short — when in doubt, let the action prune.
export const PRUNE_EXCLUDED_ACTION_TYPES = new Set<string>([
  "ProjectSlice/pruneOutput", // prevent dispatch loop
  "ProjectSlice/setProject", // loads a full project that may already have valid output
  "ProjectSlice/setActiveLCOs", // derived UI state, not a calculation input
  "ProjectSlice/AddMaterialToMtrlDb", // populates lookup db; does not change what the column references
  "ProjectSlice/UpsertMaterialInMtrlDb",
  "ProjectSlice/AddSectionToSecDb",
  "ProjectSlice/updateLoadCaseVisuals", // visual-only settings
  // Concrete fire settings a project arrived without, written in one dispatch by
  // SXInputConcreteFireSettings on mount. Repairing a project on open must not wipe the output it
  // was saved with. The setters the user's own edits go through — setFireDuration,
  // setFireAggregate, setFireManufacturing, setFireUse02Epsilon, setFireExposureSide — are
  // deliberately NOT listed: they are input changes and prune like any other. They were listed
  // once, to cover the same mount-time defaulting, and the cost was that changing the fire
  // duration or an exposed side left a stale code check on screen as if it still applied.
  // (This still auto-saves — it is not in SAVE_EXCLUDED.)
  "ProjectSlice/initConcreteFireDefaults",
  "ProjectSlice/setAutoDesign", // design preferences only — do not affect analysis output
  "ProjectSlice/setAutoDesignLimitUtilization", // design target for a future run — does not affect current output
  // NOTE: ProjectSlice/applyRecommendedDesign is deliberately NOT listed. It used to be, so the
  // output the design engine returned would survive into the controls report and spare the user a
  // run. Approving now re-runs the analysis and then the code check over what was applied (see
  // SXDesignReviewPanel), and those controls are the better ones — the design engine compares its
  // candidates against the section forces of an analysis it never re-runs, so its own output was
  // never computed for the section it settled on. Keeping it would only pre-populate the report
  // with controls the re-run replaces moments later, so it prunes like any other input change and
  // the report fills from the re-run.
  // ProjectSlice/applyVerifiedDesign, on the other hand, IS listed: the server analysed and code
  // checked that project before returning it (#442), so its output is the re-run's result already.
  // It is still saved — approval is when the applied input is persisted.
  "ProjectSlice/applyVerifiedDesign",
  // Clearing a toast message is not an input change. App dispatches these immediately after
  // rendering the alert, so pruning here would wipe the very results the calculation just
  // produced (and the debounced save would then persist the empty output).
  "ProjectSlice/dismissCalcError",
  "ProjectSlice/dismissCalcWarning",
  // Clears output.carbon and nothing else — pruning here would wipe the rest of output as a side
  // effect of resetting one field.
  "ProjectSlice/resetCarbonFootprint",
]);

// The carbon call is per input change, so it is debounced tighter than the save: long enough to
// coalesce a spinner being clicked repeatedly or a diameter being typed digit by digit, short
// enough that the figure feels like it belongs to the edit that caused it.
const CARBON_DEBOUNCE_MS = 400;

// The calculation routes that persist the project themselves: /analyse and /codecheck both save the
// bumped revision before calculating and the results after (see saveProjectToDatabase). /design does
// not persist - its result is staged for approval - so a save during a design run races nothing.
//
// While one of those runs, the debounced save is held rather than sent. Two reasons. It would race
// the route's own writes for the same file, and upstream those are read-modify-writes guarded by a
// rowversion, so one of the two comes back a 500 and takes the calculation down with it. And this
// payload has had its output pruned locally by the input change that scheduled it, so landing it
// after the results save would silently replace freshly calculated results with an empty output.
// Held, not dropped: the input change still has to reach the server, and once the run settles the
// save carries the results the run just produced.

const calculationIsRunning = (state: any): boolean =>
  state.ProjectSlice.analysisStatus === "loading" || state.ProjectSlice.codeCheckStatus === "loading";

const createSyncMiddleware = ({ debounceTime = 1000 } = {}) => {
  let timeoutid: ReturnType<typeof setTimeout>;
  let carbonTimeoutId: ReturnType<typeof setTimeout>;

  return (store: any) => (next: any) => (action: any) => {
    // Taken before the reducer runs, so a change of material can be detected below.
    const projectBefore = store.getState().ProjectSlice.project;
    const before = { guid: projectBefore?.guid, material: carbonMaterialOf(projectBefore) };

    const result = next(action);
    const isProjectAction = typeof action.type === "string" && action.type.startsWith("ProjectSlice/");

    // Keep the carbon readout current while the user edits, instead of only after a code check.
    // The decision itself — reset, recalculate, or leave alone — lives in ./carbonTriggers.
    const project = store.getState().ProjectSlice.project;
    const { reset, update } = decideCarbonWork({
      actionType: action.type,
      before,
      after: { guid: project?.guid, material: carbonMaterialOf(project) },
      hasCarbon: projectHasCarbonFigure(project),
    });

    if (reset) {
      store.dispatch(ProjectSlice.actions.resetCarbonFootprint());
    }

    if (update) {
      clearTimeout(carbonTimeoutId);
      carbonTimeoutId = setTimeout(() => {
        if (!store.getState().ProjectSlice.project) return;
        store.dispatch(updateCarbonFootprint());
      }, CARBON_DEBOUNCE_MS);
    }

    // Invalidate stale calculation output: any input change drops output computed from the old input.
    if (isProjectAction && !PRUNE_EXCLUDED_ACTION_TYPES.has(action.type)) {
      store.dispatch(ProjectSlice.actions.pruneOutput());
    }

    // Persist input changes: any input change schedules a debounced PUT of the whole project.
    if (isProjectAction && !SAVE_EXCLUDED_ACTION_TYPES.has(action.type)) {
      clearTimeout(timeoutid);
      const save = () => {
        const state = store.getState();
        const project = state.ProjectSlice.project;
        if (!project?.guid) return;
        // Re-arm instead of saving while a calculation owns this project's file. Re-arming rather
        // than subscribing keeps the hold in one place, and one extra debounce period of delay on
        // a save the user cannot observe is not worth a listener for.
        if (calculationIsRunning(state)) {
          timeoutid = setTimeout(save, debounceTime);
          return;
        }
        store.dispatch(updateProject({ guid: project.guid, project }));

        const analyseOnSave = state.UserSlice.user?.settings?.analyseOnSave ?? false;
        // Only when the saved project has no analysis to speak of. Output present means nothing has
        // invalidated it since the last run — every real input change prunes it above — so there is
        // nothing to re-analyse, and analysing anyway would replace whatever is on screen with
        // analysis-only output. That matters for a design approval, which applies a result and then
        // runs the analysis and the code check over it itself (see SXDesignReviewPanel): the save it
        // scheduled lands afterwards, and a re-analysis here would wipe the code check the user was
        // just handed.
        const hasAnalysisOutput = !!project?.data?.data?.output?.lcombs?.length;
        // A bar layout that fails the exact-bar check is most likely still being built, so the
        // analysis nobody asked for is skipped rather than failed with a toast. Pressing Analysis
        // runs the same check and does say what is wrong (see exactBarChecks).
        const barsUnfinished = exactBarCheckMessage(project?.data?.data?.input) !== null;
        if (analyseOnSave && !hasAnalysisOutput && !barsUnfinished) {
          store.dispatch(analyseProject({ file: project }));
        }
      };
      timeoutid = setTimeout(save, debounceTime);
    }

    return result;
  };
};

export default createSyncMiddleware;
